import { useTranslate } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Tag, Form, Row, Col, Segmented } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { UserRole, type UserOut } from '@/api/generated/schemas';
import { UserRoleTag } from '@/components/iam/tags';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import type { CrudFilter } from '@refinedev/core';
import { SelectFilter } from '@/components/common/SelectFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { getFilterValue, isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

const ANONYMITY_OPTIONS = ['all', 'registered', 'anonymous'] as const;
type Anonymity = (typeof ANONYMITY_OPTIONS)[number];

export const UserList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<UserOut>({
      pagination: { pageSize: 20 },
      resource: 'iam.users',
      onSearch,
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
      // Registered users by default; "all" clears the filter explicitly.
      filters: {
        initial: [{ field: 'is_anonymous', operator: 'eq', value: false }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  // Read back from the filters, so the segment follows `is_anonymous[eq]`.
  const anonymousFilter = getFilterValue<boolean | string>(
    filters as CrudFilter[],
    'is_anonymous',
    'eq'
  );
  const anonymity: Anonymity =
    anonymousFilter == null
      ? 'all'
      : String(anonymousFilter) === 'true'
        ? 'anonymous'
        : 'registered';

  const handleAnonymityChange = (value: Anonymity) =>
    setFilters(
      [
        {
          field: 'is_anonymous',
          operator: 'eq',
          value: value === 'all' ? undefined : value === 'anonymous',
        },
      ],
      'merge'
    );

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('iam.users.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col flex="none">
            <Segmented<Anonymity>
              value={anonymity}
              onChange={handleAnonymityChange}
              options={ANONYMITY_OPTIONS.map((value) => ({
                label: t(`iam.users.anonymity.${value}`),
                value,
              }))}
            />
          </Col>
        </Row>
      </Form>

      <Table {...rowNav('iam.users', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column dataIndex="email" title={t('iam.users.fields.email')} />

        <Table.Column
          dataIndex="role"
          title={t('iam.users.fields.role')}
          render={(value: string) => <UserRoleTag role={value} />}
          filtered={isFieldFiltered(filters as CrudFilter[], 'role')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="role"
              minWidth={200}
              placeholder={t('iam.users.filter.role')}
              options={Object.values(UserRole).map((value) => ({
                label: t(`iam.users.roles.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="is_verified"
          title={t('iam.users.fields.is_verified')}
          render={(value: boolean) => (
            <Tag color={value ? 'green' : 'red'}>{value ? 'Yes' : 'No'}</Tag>
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'is_verified')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter<boolean>
              field="is_verified"
              minWidth={150}
              placeholder={t('iam.users.filter.is_verified')}
              options={[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="is_active"
          title={t('iam.users.fields.is_active')}
          render={(value: boolean) => (
            <Tag color={value ? 'green' : 'red'}>
              {value ? 'Active' : 'Inactive'}
            </Tag>
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'is_active')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter<boolean>
              field="is_active"
              minWidth={150}
              placeholder={t('iam.users.filter.is_active')}
              options={[
                { label: 'Active', value: true },
                { label: 'Inactive', value: false },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
